import React, { useEffect, useState } from 'react';
import { Box, Flex, HStack, VStack, Text, Button, useToast, useClipboard } from '@chakra-ui/react';
import { CopyIcon } from '@chakra-ui/icons';
import BottomNavBar from '../../../BottomNavBar';
import MainChildHeader from '../MainChildHeader';
import { AccountContext } from '../../../AccountContext';
import { useContext } from 'react';
import ModalEmpreendedor from './ModalEmpreendedor';
import Loading from 'components/Loading';
import { useNavigate } from 'react-router-dom';
import ModalProvaDeVida from './ModalProvaDeVida';
import useRecebedor from '../../../../hooks/useRecebedor';
import ModalAvisoCadastroDadosBancarios from './ModalAvisoCadastroDadosBancarios';
import usePredefinedToastNotify from 'hooks/usePredefinedToastNotify';
import { GoAlertFill, GoClock } from 'react-icons/go';
import { FiArrowDownLeft, FiArrowRight, FiInfo, FiChevronRight, FiCheck, FiUserPlus } from 'react-icons/fi';
import { BsReceipt, BsBook, BsBank, BsCrosshair, BsHourglass } from 'react-icons/bs';
import { MdOutlineAttachMoney, MdOutlineConfirmationNumber, MdOutlineIosShare } from 'react-icons/md';
import { api } from 'api/api';
import { converterCentavosParaReais } from 'components/Utils/Functions/MoneyFunctions';

const ENDPOINT =
  process.env.REACT_APP_ENVIRONMENT === 'production'
    ? process.env.REACT_APP_BASE_URL_API_SERVER_PROD
    : process.env.REACT_APP_BASE_URL_API_SERVER_TEST;

const useViewModel = () => {
  const { user, setPage, setUser } = useContext(AccountContext);
  setPage('Revendedor');

  const [contratos, setContratos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salesTicket, setSalesTicket] = useState(null);
  const [atividade, setAtividade] = useState([]);
  const [disponivelParaSaque, setDisponivelParaSaque] = useState(null);

  const notify = usePredefinedToastNotify();
  const navigate = useNavigate();

  // PagarMe: recipient_id já vem populado no login (tb_empreendedor_dados_bancarios.recipient_id_pagarme)
  const recebedor = useRecebedor(user?.recipient_id);

  const fetchContratos = async () => {
    const url = `${ENDPOINT}/entrepreneur/extrato/contratos/${user.id_vendedor}`;
    const response = await api(url, {
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      method: 'GET',
    });
    const fetchData = await response.json();
    setUser((prevUser) => ({ ...prevUser, contratosEmpreendedor: fetchData.data.contratos }));
    setContratos(fetchData.data.contratos);
  };

  const fetchEmpresas = async () => {
    const url = `${ENDPOINT}/entrepreneur/extrato/credenciados/${user.id_vendedor}`;
    const response = await api(url, {
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      method: 'GET',
    });
    const fetchData = await response.json();
    setUser((prevUser) => ({ ...prevUser, empresasEmpreendedor: fetchData.data }));
    setEmpresas(fetchData.data);
  };

  const fetchVendedores = async () => {
    const url = `${ENDPOINT}/entrepreneur/extrato/vendedores/${user.id_vendedor}`;
    const response = await api(url, {
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      method: 'GET',
    });
    const fetchData = await response.json();
    setUser((prevUser) => ({ ...prevUser, vendedoresEmpreendedor: fetchData.data.result }));
    setVendedores(fetchData.data.result);
  };

  const fetchSalesTicket = async () => {
    if (!user.id_vendedor) return;
    try {
      const res = await api(`${ENDPOINT}/entrepreneur/sales`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vendedor_id: user.id_vendedor, username: user.username || user.cpf }),
      });
      const data = await res.json();
      if (data?.success) setSalesTicket(data.data);
    } catch (e) {
      console.log('fetchSalesTicket error:', e);
    }
  };

  const fetchAtividade = async () => {
    if (!user.id_vendedor) return;
    try {
      const res = await api(`${ENDPOINT}/entrepreneur/atividade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vendedor_id: user.id_vendedor, username: user.username || user.cpf }),
      });
      const data = await res.json();
      if (data?.success) setAtividade(data.data);
    } catch (e) {
      console.log('fetchAtividade error:', e);
    }
  };

  // Mesmo endpoint que a tela de Ganhos Totais usa pro saldo disponível pra saque
  const fetchSaldoDisponivel = async () => {
    if (!user.recipient_id) return;
    try {
      const res = await api(`${ENDPOINT}/entrepreneur/balance/${user.recipient_id}`, {
        method: 'GET',
        credentials: 'include',
      });
      const data = await res.json();
      const availableAmount = data?.recipientBalance?.data?.available_amount;
      if (availableAmount != null) setDisponivelParaSaque(converterCentavosParaReais(availableAmount));
    } catch (e) {
      console.log('fetchSaldoDisponivel error:', e);
    }
  };

  async function fetchDatas() {
    if (user.contratosEmpreendedor && Array.isArray(user.contratosEmpreendedor) && user.contratosEmpreendedor.length > 0) {
      setContratos(user.contratosEmpreendedor);
    } else {
      await fetchContratos();
    }
    if (user.empresasEmpreendedor && Array.isArray(user.empresasEmpreendedor) && user.empresasEmpreendedor.length > 0) {
      setEmpresas(user.empresasEmpreendedor);
    } else {
      await fetchEmpresas();
    }
    if (user.vendedoresEmpreendedor && Array.isArray(user.vendedoresEmpreendedor) && user.vendedoresEmpreendedor.length > 0) {
      setVendedores(user.vendedoresEmpreendedor);
    } else {
      await fetchVendedores();
    }
    setLoading(false);
  }

  const handlerOnPressGerarQrCode = () => navigate('/empreendedor/qrcode');

  const openChatSuporteWhatsapp = (msg = '') => {
    window.open(`https://wa.me/558002802133?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handlePreencherDadosBancarios = () => navigate('/empreendedor/dados-bancarios');

  useEffect(() => {
    fetchDatas();
    fetchSalesTicket();
    fetchAtividade();
    fetchSaldoDisponivel();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    notify,
    user,
    contratos,
    empresas,
    vendedores,
    loading,
    salesTicket,
    atividade,
    disponivelParaSaque,
    recebedor,
    handlerOnPressGerarQrCode,
    openChatSuporteWhatsapp,
    handlePreencherDadosBancarios,
    navigate,
  };
};

function formatBRL(value) {
  if (value == null) return '...';
  return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const diffH = Math.floor((Date.now() - date) / 3600000);
  const diffD = Math.floor(diffH / 24);
  if (diffH < 1) return 'agora';
  if (diffH < 24) return `${diffH}h`;
  if (diffD === 1) return 'Ontem';
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function BalanceCard({ salesTicket, disponivelParaSaque, navigate }) {
  const mesAtual = new Date().toLocaleString('pt-BR', { month: 'long' });
  return (
    <Box bgGradient="linear(135deg, #2BA89E, #1F7D75)" borderRadius="20px" p="18px" w="100%">
      <Flex justifyContent="space-between" alignItems="center">
        <HStack spacing="8px">
          <FiArrowDownLeft color="rgba(255,255,255,0.8)" size={16} />
          <Text color="rgba(255,255,255,0.8)" fontSize="13px" fontWeight="500">
            Recebido em {mesAtual}
          </Text>
        </HStack>
      </Flex>

      <HStack spacing="6px" alignItems="flex-end" mt="8px">
        <Text color="rgba(255,255,255,0.8)" fontSize="16px" fontWeight="600" mb="2px">
          R$
        </Text>
        <Text color="white" fontSize="30px" fontWeight="700" lineHeight="1">
          {formatBRL(salesTicket?.valor_total)}
        </Text>
      </HStack>

      <Box h="1px" bg="rgba(255,255,255,0.15)" my="14px" />

      <Flex justifyContent="space-between" alignItems="center">
        <HStack spacing="10px">
          <Flex bg="rgba(255,255,255,0.15)" borderRadius="10px" w="36px" h="36px" alignItems="center" justifyContent="center">
            <BsHourglass color="white" size={18} />
          </Flex>
          <VStack spacing="1" alignItems="flex-start">
            <Text color="rgba(255,255,255,0.8)" fontSize="11px" fontWeight="500">
              A receber
            </Text>
            <Text color="white" fontSize="15px" fontWeight="700">
              R$ {formatBRL(disponivelParaSaque)}
            </Text>
          </VStack>
        </HStack>

        <Box as="button" bg="white" borderRadius="12px" px="14px" py="10px" onClick={() => navigate('/mdv/ganhos-totais')}>
          <HStack spacing="6px">
            <Text color="#1F7D75" fontSize="13px" fontWeight="600">
              Ver extrato
            </Text>
            <FiArrowRight color="#1F7D75" size={14} />
          </HStack>
        </Box>
      </Flex>

      <HStack spacing="6px" mt="10px">
        <FiInfo color="rgba(255,255,255,0.8)" size={12} />
        <Text color="rgba(255,255,255,0.8)" fontSize="10px" fontWeight="500">
          Transferido conforme compensação (Pix, boleto ou cartão)
        </Text>
      </HStack>
    </Box>
  );
}

function formatCompact(value) {
  if (value == null) return '...';
  const n = Number(value);
  if (n >= 1000) return `R$ ${(n / 1000).toFixed(1).replace('.', ',')}k`;
  return `R$ ${n.toFixed(2).replace('.', ',')}`;
}

function KpiRow({ salesTicket }) {
  const kpis = [
    {
      icon: <MdOutlineConfirmationNumber color="#1F7D75" size={17} />,
      iconBg: '#E8F5F3',
      value: formatCompact(salesTicket?.ticket_medio),
      label: 'Ticket médio',
    },
    {
      icon: <MdOutlineAttachMoney color="#F97316" size={17} />,
      iconBg: '#FEEEDF',
      value: formatCompact(salesTicket?.valor_total),
      label: 'Vendas do mês',
    },
    {
      icon: <BsCrosshair color="#8B5CF6" size={15} />,
      iconBg: '#EFEAFD',
      value: salesTicket ? `${salesTicket.taxa_de_conversao}%` : '...',
      label: 'Conversão',
    },
  ];

  return (
    <HStack spacing="10px" w="100%" alignItems="stretch">
      {kpis.map((kpi, i) => (
        <Box key={i} bg="white" borderRadius="16px" p="14px" border="1px solid #EEF1F5" flex={1}>
          <Flex mb="6px">
            <Flex bg={kpi.iconBg} borderRadius="10px" w="32px" h="32px" alignItems="center" justifyContent="center">
              {kpi.icon}
            </Flex>
          </Flex>
          <Text color="#0F1B2A" fontSize="18px" fontWeight="700" lineHeight="1.1">
            {kpi.value}
          </Text>
          <Text color="#5B6B7A" fontSize="11px" fontWeight="500" mt="2px">
            {kpi.label}
          </Text>
        </Box>
      ))}
    </HStack>
  );
}

function QuickActions({ navigate }) {
  const actions = [
    [
      {
        icon: <BsReceipt color="#F97316" size={22} />,
        iconBg: '#FEEEDF',
        title: 'Extrato',
        desc: 'Saldo e saque',
        onPress: () => navigate('/mdv/ganhos-totais'),
      },
    ],
    [
      {
        icon: <BsBook color="#3B82F6" size={22} />,
        iconBg: '#E4EEFE',
        title: 'Material',
        desc: 'Apoio para vendas',
        onPress: () => navigate('/emprendedor/materialapoio'),
      },
      {
        icon: <BsBank color="#8B5CF6" size={22} />,
        iconBg: '#EFEAFD',
        title: 'Dados bancários',
        desc: 'Recebimento',
        onPress: () => navigate('/empreendedor/dados-bancarios'),
      },
    ],
  ];

  return (
    <VStack spacing="14px" w="100%" alignItems="flex-start">
      <Flex justifyContent="space-between" alignItems="center" w="100%">
        <Text color="#0F1B2A" fontSize="17px" fontWeight="700">
          Acesso rápido
        </Text>
      </Flex>

      <VStack spacing="12px" w="100%">
        {actions.map((row, ri) => (
          <HStack key={ri} spacing="12px" w="100%" alignItems="stretch">
            {row.map((action, ai) => (
              <Box
                key={ai}
                as="button"
                bg="white"
                borderRadius="18px"
                p="16px"
                border="1px solid #EEF1F5"
                flex={1}
                h="130px"
                onClick={action.onPress}
                textAlign="left"
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                alignItems="flex-start"
                cursor="pointer"
                _active={{ opacity: 0.8 }}
              >
                <Flex bg={action.iconBg} borderRadius="12px" w="44px" h="44px" alignItems="center" justifyContent="center">
                  {action.icon}
                </Flex>
                <VStack spacing="2px" alignItems="flex-start" w="100%">
                  <Text color="#0F1B2A" fontSize="14px" fontWeight="700">
                    {action.title}
                  </Text>
                  <Text color="#5B6B7A" fontSize="11px" fontWeight="500">
                    {action.desc}
                  </Text>
                </VStack>
              </Box>
            ))}
          </HStack>
        ))}
      </VStack>
    </VStack>
  );
}

function AtividadeRecente({ atividade }) {
  const navigate = useNavigate();
  return (
    <VStack spacing="14px" w="100%" alignItems="flex-start">
      <Flex justifyContent="space-between" alignItems="center" w="100%">
        <Text color="#0F1B2A" fontSize="17px" fontWeight="700">
          Atividade recente
        </Text>
        <VStack style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
          <Text
            color="#1F7D75"
            fontSize="13px"
            fontWeight="600"
            onClick={() => navigate('/empreendedor/contratos-vendidos')}
            cursor="pointer"
          >
            Ver tudo
          </Text>
          <FiChevronRight color="#1F7D75" size={14} />
        </VStack>
      </Flex>

      {!atividade || atividade.length === 0 ? (
        <Text color="#9AA5B1" fontSize="13px" textAlign="center" w="100%" py="8px">
          Nenhuma atividade recente.
        </Text>
      ) : (
        <Box bg="white" borderRadius="18px" border="1px solid #EEF1F5" w="100%" overflow="hidden" p="6px">
          {atividade.slice(0, 5).map((item, i) => {
            const pago = item.pago === true || item.pago === 'true';
            const partes = item.cliente ? item.cliente.split(' ') : [];
            const nomeAbrev = partes.length > 1 ? `${partes[0]} ${partes[1][0]}.` : partes[0] || '—';
            const valor = item.valor != null ? `+R$ ${Number(item.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '';
            const quando = timeAgo(item.data_cadastro);

            return (
              <React.Fragment key={i}>
                {i > 0 && <Box h="1px" bg="#EEF1F5" mx="10px" />}
                <Flex alignItems="center" gap="12px" px="10px" py="12px">
                  <Flex
                    bg={pago ? '#DCFCE7' : '#EFF6FF'}
                    borderRadius="12px"
                    w="40px"
                    h="40px"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    {pago ? <FiCheck color="#16A34A" size={18} /> : <FiUserPlus color="#3B82F6" size={18} />}
                  </Flex>

                  <VStack spacing="2px" alignItems="flex-start" flex={1} minW={0}>
                    <Text color="#0F1B2A" fontSize="13px" fontWeight="600" noOfLines={1}>
                      {pago ? 'Novo contrato confirmado' : 'Indicação recebida'}
                    </Text>
                    <Text color="#5B6B7A" fontSize="11px" fontWeight="500" noOfLines={1}>
                      {nomeAbrev} • {item.plano || 'Plano'}
                    </Text>
                  </VStack>

                  <VStack spacing="2px" alignItems="flex-end" flexShrink={0}>
                    {pago ? (
                      <Text color="#16A34A" fontSize="13px" fontWeight="700">
                        {valor}
                      </Text>
                    ) : (
                      <Box bg="#FFF7ED" borderRadius="8px" px="8px" py="3px">
                        <Text color="#F97316" fontSize="10px" fontWeight="600">
                          Pendente
                        </Text>
                      </Box>
                    )}
                    <Text color="#9AA5B1" fontSize="10px" fontWeight="500">
                      {quando}
                    </Text>
                  </VStack>
                </Flex>
              </React.Fragment>
            );
          })}
        </Box>
      )}
    </VStack>
  );
}

function CodigoIndicacao({ user }) {
  const toast = useToast();
  const codigo = String(user.id_vendedor ?? '');
  const mensagem = `Olá, me chamo ${user.name}, sou revendedor Deovita!\n\nNão tem plano? DeoVita é o plano!\n\nUse meu código de indicação na sua adesão:\n${codigo}`;
  const { onCopy } = useClipboard(mensagem);

  const handleCopiar = () => {
    onCopy();
    toast({
      title: 'Copiado!',
      description: 'Mensagem com link e código copiada.',
      status: 'success',
      duration: 2500,
      isClosable: true,
      position: 'bottom',
      containerStyle: { marginBottom: '80px' },
    });
  };

  const handleCompartilhar = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'DeoVita', text: mensagem });
      } catch {
        handleCopiar();
      }
    } else {
      handleCopiar();
    }
  };

  return (
    <Box bg="white" borderRadius="18px" border="1px solid #EEF1F5" p="18px" w="100%">
      <HStack spacing="8px" mb="12px">
        <Flex bg="#E8F5F3" borderRadius="10px" w="32px" h="32px" alignItems="center" justifyContent="center">
          <MdOutlineConfirmationNumber color="#1F7D75" size={17} />
        </Flex>
        <Text color="#0F1B2A" fontSize="15px" fontWeight="700">
          Código de indicação
        </Text>
      </HStack>

      <Box bg="#F4F7F9" borderRadius="12px" py="14px" px="16px" mb="14px" textAlign="center">
        <Text color="#1F7D75" fontSize="28px" fontWeight="800" letterSpacing="6px">
          {codigo || '—'}
        </Text>
      </Box>

      <HStack spacing="10px">
        <Button flex={1} colorScheme="teal" variant="solid" size="sm" borderRadius="12px" leftIcon={<CopyIcon />} onClick={handleCopiar}>
          Copiar código
        </Button>
        <Button
          flex={1}
          colorScheme="teal"
          variant="outline"
          size="sm"
          borderRadius="12px"
          leftIcon={<MdOutlineIosShare size={16} />}
          onClick={handleCompartilhar}
        >
          Compartilhar
        </Button>
      </HStack>
    </Box>
  );
}

/* ─── Main ─── */

function Main() {
  const {
    notify,
    user,
    loading,
    salesTicket,
    atividade,
    disponivelParaSaque,
    recebedor,
    openChatSuporteWhatsapp,
    handlePreencherDadosBancarios,
    navigate,
  } = useViewModel();

  if (user.empreendedor === false) return <ModalEmpreendedor />;

  const LoadingScreen = (
    <>
      <MainChildHeader />
      <Loading />
      <BottomNavBar name="empreendedor" />
    </>
  );

  if (loading) return LoadingScreen;

  if (recebedor.isRecebedorRecemCadastrado) {
    return (
      <>
        <MainChildHeader />
        <ModalAvisoCadastroDadosBancarios
          headingTitle="Cadastro de conta bancária"
          bodyText="Realize o cadastro da sua conta bancária para começar a ser um revendor."
          icon={<GoAlertFill color="orange" size={60} style={{ marginTop: '1em' }} />}
          isOpen
          buttonText="Cadastrar conta bancária"
          onButtonPress={handlePreencherDadosBancarios}
        />
        <BottomNavBar name="empreendedor" />
      </>
    );
  }

  if (recebedor.isPedidoDeAfiliacaoEmAnalise) {
    return (
      <>
        <MainChildHeader />
        <ModalAvisoCadastroDadosBancarios
          headingTitle="Cadastro de conta bancária em análise"
          bodyText="Aguarde a análise de risco dos seus dados bancários."
          icon={<GoClock color="orange" size={60} style={{ marginTop: '1em' }} />}
          isOpen
        />
        <BottomNavBar name="empreendedor" />
      </>
    );
  }

  if (recebedor.isRecebedorRejeitadoNaAnaliseDeRisco) {
    return (
      <>
        <MainChildHeader />
        <ModalAvisoCadastroDadosBancarios
          headingTitle="Cadastro de conta bancária recusado"
          bodyText="Seu cadastro de conta bancária foi recusado, contate o suporte."
          icon={<GoAlertFill color="orange" size={60} style={{ marginTop: '1em' }} />}
          isOpen
          buttonText="Falar com o suporte"
          onButtonPress={() =>
            openChatSuporteWhatsapp(
              'Olá, estou com dificuldades para cadastrar minha conta bancária, fui rejeitado na Analise de Risco. Pode me ajudar?',
            )
          }
        />
        <BottomNavBar name="empreendedor" />
      </>
    );
  }

  if (recebedor.isRecebedorPrecisaFazerProvaDeVida) {
    return (
      <>
        <MainChildHeader />
        <Loading />
        <ModalProvaDeVida />
        <BottomNavBar name="empreendedor" />
      </>
    );
  }

  if (recebedor.isProvaDeVidaRespondidaAguardandoAnalise) {
    return (
      <>
        <MainChildHeader />
        <ModalAvisoCadastroDadosBancarios
          headingTitle="Prova de vida em análise"
          bodyText="Aguarde a análise da sua prova de vida."
          icon={<GoClock color="orange" size={60} style={{ marginTop: '1em' }} />}
          isOpen
        />
        <BottomNavBar name="empreendedor" />
      </>
    );
  }

  if (recebedor.isPedidoDeAfiliacaoEmAnaliseManualDeRisco) {
    return (
      <>
        <MainChildHeader />
        <ModalAvisoCadastroDadosBancarios
          headingTitle="Cadastro de conta bancária em análise"
          bodyText="Aguarde a análise de risco dos seus dados bancários."
          icon={<GoClock color="orange" size={60} style={{ marginTop: '1em' }} />}
          isOpen
        />
        <BottomNavBar name="empreendedor" />
      </>
    );
  }

  if (recebedor.isRecebedorReprovadoNaProvaDeVida) {
    return (
      <>
        <MainChildHeader />
        <ModalAvisoCadastroDadosBancarios
          headingTitle="Prova de vida recusada"
          bodyText="Sua prova de vida foi recusada, contate o suporte."
          icon={<GoAlertFill color="orange" size={60} style={{ marginTop: '1em' }} />}
          isOpen
          buttonText="Falar com o suporte"
          onButtonPress={() =>
            openChatSuporteWhatsapp(
              'Olá, estou com dificuldades para cadastrar minha conta bancária, fui rejeitado na Analise da Prova de Vida. Pode me ajudar?',
            )
          }
        />
        <BottomNavBar name="empreendedor" />
      </>
    );
  }

  if (recebedor.isRecebedorAprovadoNaProvaDeVida) {
    notify.success('Dados do empreendedor', 'Dados carregados com sucesso!');
  }

  return (
    <Box bg="#F4F7F9" minH="100vh" display="flex" flexDirection="column">
      <MainChildHeader />

      <Box overflowY="auto" flex={1} pb="90px">
        <VStack spacing="22px" px="20px" pt="20px" pb="24px" w="100%">
          <BalanceCard salesTicket={salesTicket} disponivelParaSaque={disponivelParaSaque} navigate={navigate} />
          <KpiRow salesTicket={salesTicket} />
          <CodigoIndicacao user={user} />
          <QuickActions navigate={navigate} />
          <AtividadeRecente atividade={atividade} />
        </VStack>
      </Box>

      <BottomNavBar name="empreendedor" />
    </Box>
  );
}

export default Main;
